import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Dice5, ListChecks, ScrollText, Users, ExternalLink, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/sortiven-logo.png";

const ROL_LABEL = { admin: "Administrador", operador: "Operador", auditor: "Auditor/Veedor" };

export const ConsoleLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [
    { to: "/consola", label: "Resumen", icon: LayoutDashboard, end: true },
    { to: "/consola/juegos", label: "Juegos", icon: Dice5 },
    { to: "/consola/sorteos", label: "Sorteos", icon: ListChecks },
    { to: "/consola/auditoria", label: "Auditoría", icon: ScrollText },
  ];
  if (user?.rol === "admin") nav.push({ to: "/consola/usuarios", label: "Usuarios", icon: Users });

  const onLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground" data-theme="institutional">
      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-[260px] shrink-0 flex-col border-r border-border bg-card lg:flex">
          <div className="flex flex-col gap-1.5 border-b border-border px-5 py-4">
            <Link to="/"><img src={logo} alt="SORTIVEN" className="h-9 w-auto" /></Link>
            <p className="text-[11px] text-muted-foreground">Consola · Operación &amp; Auditoría</p>
          </div>
          <nav className="flex-1 space-y-1 p-3">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`console-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}>
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="space-y-3 border-t border-border p-3">
            <div className="rounded-lg bg-secondary px-3 py-2">
              <p className="truncate text-sm font-medium" data-testid="console-user-name">{user?.nombre}</p>
              <p className="text-[11px] text-muted-foreground">{ROL_LABEL[user?.rol] || user?.rol}</p>
            </div>
            <Link to="/" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"><ExternalLink className="h-4 w-4" /> Ver sitio público</Link>
            <Button variant="outline" className="w-full gap-2" onClick={onLogout} data-testid="console-logout-button"><LogOut className="h-4 w-4" /> Cerrar sesión</Button>
          </div>
        </aside>
        <div className="flex-1">
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/85 px-4 backdrop-blur sm:px-6 lg:hidden">
            <span className="font-display font-bold">Consola</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{ROL_LABEL[user?.rol]}</span>
              <Button size="sm" variant="ghost" onClick={onLogout}><LogOut className="h-4 w-4" /></Button>
            </div>
          </header>
          <div className="flex gap-1 overflow-x-auto border-b border-border bg-card px-3 py-2 lg:hidden">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};
